import React, { useEffect, useRef, useState } from "react";

const COLORS = ["green", "yellow", "red"];

const COLOR_META = {
  green: {
    label: "Good",
    short: "G",
    dot: "#22c55e",
    bg: "#ecfdf5",
    border: "#86efac",
    text: "#166534",
  },
  yellow: {
    label: "Almost",
    short: "Y",
    dot: "#facc15",
    bg: "#fffbeb",
    border: "#fde68a",
    text: "#92400e",
  },
  red: {
    label: "Needs work",
    short: "R",
    dot: "#ef4444",
    bg: "#fef2f2",
    border: "#fca5a5",
    text: "#991b1b",
  },
};

const isRateableChar = (char) => /[\p{L}\p{N}]/u.test(char);

const getAudioFetchUrl = (audioUrl) => {
  if (!audioUrl) return null;
  if (audioUrl.startsWith("http")) return audioUrl;
  return `${process.env.REACT_APP_USER_API_URL}${audioUrl}`;
};

const splitWords = (text = "") =>
  String(text)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

const buildRatings = (text = "") =>
  splitWords(text).map((word, wordIndex) => ({
    word,
    index: wordIndex,
    rating: "green",
    letters: Array.from(word).map((char, letterIndex) => ({
      char,
      index: letterIndex,
      rating: isRateableChar(char) ? "green" : null,
      rateable: isRateableChar(char),
    })),
  }));

function getTintStyle(rating) {
  const meta = COLOR_META[rating || "green"];

  return {
    background: `linear-gradient(135deg, ${meta.bg} 0%, #ffffff 100%)`,
    borderColor: meta.border,
  };
}

function getUserNBest(assessment) {
  return assessment?.pronunciation?.NBest?.[0] || null;
}

function getChapterDisplay(comment, payloadBase) {
  return (
    payloadBase?.chapterTitle ||
    comment?.chapterTitle ||
    comment?.file ||
    payloadBase?.file ||
    ""
  );
}

function CircleRadioGroup({ value, onChange, compact = false }) {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border bg-white shadow-sm"
      style={{
        borderColor: "#e2e8f0",
        padding: compact ? "5px 7px" : "7px 10px",
      }}
    >
      {COLORS.map((color) => {
        const active = value === color;
        const meta = COLOR_META[color];

        return (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            className="flex items-center justify-center rounded-full transition"
            style={{
              width: compact ? 26 : 34,
              height: compact ? 26 : 34,
              border: `2px solid ${active ? meta.dot : "#e5e7eb"}`,
              background: active ? meta.dot : "#ffffff",
              color: active ? "#ffffff" : meta.dot,
              boxShadow: active ? `0 8px 18px ${meta.dot}33` : "none",
              fontWeight: 900,
              fontSize: compact ? 11 : 12,
              lineHeight: 1,
            }}
            title={meta.label}
          >
            {meta.short}
          </button>
        );
      })}
    </div>
  );
}

function LetterButton({ letter, onClick }) {
  if (!letter.rateable) {
    return (
      <span
        className="flex h-9 min-w-5 select-none items-center justify-center px-1 text-sm font-black text-slate-500"
        title="Punctuation is not rated"
      >
        {letter.char}
      </span>
    );
  }

  const meta = COLOR_META[letter.rating || "green"];

  return (
    <button
      type="button"
      onClick={onClick}
      className="select-none rounded-xl border font-black transition hover:-translate-y-0.5"
      style={{
        minWidth: 32,
        height: 36,
        padding: "0 10px",
        background: meta.bg,
        borderColor: meta.border,
        color: meta.text,
        fontSize: 15,
        boxShadow:
          letter.rating === "green" ? "none" : `0 8px 18px ${meta.dot}22`,
      }}
      title="Click to cycle: green → yellow → red"
    >
      {letter.char}
    </button>
  );
}

function RateMeCollapsedPreview({
  hasAudio,
  canPlay,
  loadingAudio,
  playRecording,
  audioError,
  onExpand,
  alreadyAnswered = false,
  chapterDisplay = "",
}) {
  return (
    <div
      className="mt-4 rounded-[24px] border px-4 py-3 shadow-sm"
      style={{
        borderColor: alreadyAnswered ? "#86efac" : "#c4b5fd",
        background: alreadyAnswered
          ? "linear-gradient(135deg, #ffffff 0%, #f0fdf4 65%, #ecfdf5 100%)"
          : "linear-gradient(135deg, #ffffff 0%, #f8f5ff 58%, #eff6ff 100%)",
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={alreadyAnswered ? undefined : onExpand}
          disabled={alreadyAnswered}
          className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default"
        >
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-white text-xl shadow-sm"
            style={{
              borderColor: alreadyAnswered ? "#86efac" : "#ddd6fe",
            }}
          >
            {alreadyAnswered ? "✓" : "🎙️"}
          </div>

          <div className="min-w-0">
            <div
              className="inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-1 text-[10px] font-black shadow-sm"
              style={{
                borderColor: alreadyAnswered ? "#86efac" : "#ddd6fe",
                color: alreadyAnswered ? "#166534" : "#5b21b6",
              }}
            >
              <span>{alreadyAnswered ? "✓" : "⚔️"}</span>
              <span>
                {alreadyAnswered ? "Already answered" : "Rate me request"}
              </span>
            </div>

            <div className="mt-1 text-sm font-black text-slate-950">
              {alreadyAnswered
                ? "Your verdict has been submitted"
                : "Awaiting human verdict"}
            </div>

            {chapterDisplay && (
              <div className="mt-1 max-w-[260px] truncate text-xs font-bold text-slate-500">
                {chapterDisplay}
              </div>
            )}
          </div>
        </button>

        {hasAudio && canPlay && (
          <button
            type="button"
            onClick={playRecording}
            disabled={loadingAudio}
            className="rounded-2xl border bg-white px-4 py-2.5 text-sm font-black shadow-sm transition hover:-translate-y-0.5 disabled:opacity-60"
            style={{
              borderColor: alreadyAnswered ? "#86efac" : "#c4b5fd",
              color: alreadyAnswered ? "#166534" : "#4c1d95",
            }}
          >
            {loadingAudio ? "Loading…" : "▶ Play"}
          </button>
        )}

        {hasAudio && !canPlay && (
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 shadow-sm">
            🔒 Log in
          </div>
        )}

        {!hasAudio && (
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-500 shadow-sm">
            No recording
          </div>
        )}

        {!alreadyAnswered && (
          <button
            type="button"
            onClick={onExpand}
            className="rounded-2xl px-4 py-2.5 text-sm font-black shadow-lg transition hover:-translate-y-0.5"
            style={{
              background: "#7c3aed",
              color: "#ffffff",
              border: "1px solid #6d28d9",
              boxShadow: "0 12px 24px rgba(124,58,237,0.25)",
              minWidth: 78,
            }}
          >
            Open
          </button>
        )}

        {alreadyAnswered && (
          <div
            className="rounded-2xl px-4 py-2.5 text-sm font-black shadow-sm"
            style={{
              background: "#dcfce7",
              color: "#166534",
              border: "1px solid #86efac",
            }}
          >
            Done
          </div>
        )}
      </div>

      {audioError && (
        <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
          {audioError}
        </div>
      )}
    </div>
  );
}

function SubmittedComparisonOverlay({ reply, onClose }) {
  const userNBest = getUserNBest(reply?.assessment);
  const humanRatings = reply?.ratings || [];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="w-full max-w-5xl rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="flex justify-between items-start p-6 border-b bg-gradient-to-r from-violet-50 to-blue-50">
          <div>
            <div className="text-xs font-bold text-violet-700 mb-2">
              Verdict submitted
            </div>

            <h2 className="text-2xl font-black text-gray-900">
              AI vs Your Rating
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-2xl text-gray-500 hover:text-black"
          >
            ×
          </button>
        </div>

        <div className="p-6 max-h-[75vh] overflow-y-auto">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="border rounded-2xl p-4">
              <div className="text-center font-bold mb-4">
                🤖 AI Assessment
              </div>

              <div className="flex flex-wrap gap-3 justify-center">
                {userNBest?.Words?.map((word, i) => {
                  if (word.PronunciationAssessment?.ErrorType === "Omission") {
                    return (
                      <div
                        key={i}
                        className="w-3 h-3 bg-gray-400 rounded-full"
                      />
                    );
                  }

                  const score =
                    word.PronunciationAssessment?.AccuracyScore || 0;

                  const color =
                    score >= 80 ? "green" : score >= 40 ? "yellow" : "red";

                  return (
                    <div
                      key={i}
                      className="border rounded-xl p-3 min-w-[80px] text-center"
                    >
                      <div className="font-bold mb-2">{word.Word}</div>

                      <div className="flex justify-center gap-1 mb-2">
                        {(word.Phonemes || []).map((ph, j) => {
                          const pScore =
                            ph.PronunciationAssessment?.AccuracyScore || score;

                          const pColor =
                            pScore >= 80
                              ? "bg-green-500"
                              : pScore >= 40
                              ? "bg-yellow-500"
                              : "bg-red-500";

                          return (
                            <div
                              key={j}
                              className={`w-2 h-2 rounded-full ${pColor}`}
                              title={pScore}
                            />
                          );
                        })}
                      </div>

                      <div
                        className={`text-white text-xs font-bold rounded px-2 py-1 ${
                          color === "green"
                            ? "bg-green-500"
                            : color === "yellow"
                            ? "bg-yellow-500"
                            : "bg-red-500"
                        }`}
                      >
                        {score}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border rounded-2xl p-4">
              <div className="text-center font-bold mb-4">
                🧑 Your Rating
              </div>

              <div className="flex flex-wrap gap-3 justify-center">
                {humanRatings.map((word) => {
                  const getColor = (rating) => {
                    if (rating === "green") {
                      return "bg-green-100 border-green-300 text-green-700";
                    }

                    if (rating === "yellow") {
                      return "bg-yellow-100 border-yellow-300 text-yellow-700";
                    }

                    return "bg-red-100 border-red-300 text-red-700";
                  };

                  return (
                    <div
                      key={word.index}
                      className={`border rounded-xl p-3 min-w-[80px] text-center ${getColor(
                        word.rating
                      )}`}
                    >
                      <div className="font-bold mb-2">{word.word}</div>

                      <div className="flex justify-center gap-1 flex-wrap">
                        {(word.letters || []).map((l) => (
                          <span
                            key={l.index}
                            className={`px-1 rounded text-xs font-bold ${getColor(
                              l.rating
                            )}`}
                          >
                            {l.char}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onClose}
              className="bg-violet-600 text-white px-6 py-2 rounded-xl font-bold"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RateMeCommentBlock({
  comment,
  content,
  canInteract,
  payloadBase,
  anchor,
  onSubmitted,
  alreadyAnswered = false,
}) {
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [audioError, setAudioError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedReply, setSubmittedReply] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  const audioRef = useRef(null);

  const token = localStorage.getItem("token");
  const audioUrl = comment?.audio?.audio_url;
  const hasAudio = Boolean(audioUrl);
  const canPlay = Boolean(token && canInteract);

  const sentenceText = comment?.anchor?.text || anchor?.text || "";
  const chapterDisplay = getChapterDisplay(comment, payloadBase);

  const [sentenceRating, setSentenceRating] = useState("green");
  const [ratings, setRatings] = useState(() => buildRatings(sentenceText));
  const [feedbackText, setFeedbackText] = useState("");

  useEffect(() => {
    setSentenceRating("green");
    setRatings(buildRatings(sentenceText));
    setFeedbackText("");
    setSubmitError("");
    setCollapsed(true);
  }, [sentenceText, comment?.id]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();

        if (audioRef.current.src?.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }

        audioRef.current = null;
      }
    };
  }, []);

  const playRecording = async () => {
    const fetchUrl = getAudioFetchUrl(audioUrl);
    if (!fetchUrl || !canPlay) return;

    setLoadingAudio(true);
    setAudioError("");

    try {
      const res = await fetch(fetchUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401 || res.status === 403) {
        throw new Error("LOGIN_REQUIRED");
      }

      if (!res.ok) throw new Error(`Audio ${res.status}`);

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      if (audioRef.current) {
        audioRef.current.pause();

        if (audioRef.current.src?.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }
      }

      const player = new Audio(objectUrl);
      audioRef.current = player;

      player.onended = () => URL.revokeObjectURL(objectUrl);
      player.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setAudioError("Could not play recording.");
      };

      await player.play();
    } catch (e) {
      console.error("play rate me audio failed:", e);
      setAudioError(
        e.message === "LOGIN_REQUIRED"
          ? "Log in to listen and rate."
          : "Could not play recording."
      );
    } finally {
      setLoadingAudio(false);
    }
  };

  const setWordRating = (wordIndex, rating) => {
    setRatings((prev) =>
      prev.map((word) =>
        word.index === wordIndex ? { ...word, rating } : word
      )
    );
  };

  const cycleLetterRating = (wordIndex, letterIndex) => {
    setRatings((prev) =>
      prev.map((word) => {
        if (word.index !== wordIndex) return word;

        return {
          ...word,
          letters: word.letters.map((letter) => {
            if (letter.index !== letterIndex || !letter.rateable) return letter;

            const currentIndex = COLORS.indexOf(letter.rating || "green");
            const nextRating = COLORS[(currentIndex + 1) % COLORS.length];

            return { ...letter, rating: nextRating };
          }),
        };
      })
    );
  };

  const submitRating = async () => {
    if (!canInteract || !payloadBase || !anchor?.text || !comment?.id) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/rate-me-reply`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            ...payloadBase,
            chapterTitle: chapterDisplay,
            anchor,
            parentCommentId: comment.id,
            content: feedbackText.trim() || "Human pronunciation verdict",
            sentenceRating,
            ratings: ratings.map((word) => ({
              word: word.word,
              index: word.index,
              rating: word.rating,
              letters: word.letters
                .filter((letter) => letter.rateable)
                .map((letter) => ({
                  char: letter.char,
                  index: letter.index,
                  rating: letter.rating || "green",
                })),
            })),
          }),
        }
      );

      if (!res.ok) throw new Error(`Submit ${res.status}`);

      const created = await res.json();

      setSubmittedReply(created);
      setCollapsed(true);

      setSentenceRating("green");
      setRatings(buildRatings(sentenceText));
      setFeedbackText("");
    } catch (e) {
      console.error("submit rate me reply failed:", e);
      setSubmitError("Could not submit your rating.");
    } finally {
      setSubmitting(false);
    }
  };

  if (collapsed) {
    return (
      <>
        <RateMeCollapsedPreview
          hasAudio={hasAudio}
          canPlay={canPlay}
          loadingAudio={loadingAudio}
          playRecording={playRecording}
          audioError={audioError}
          onExpand={() => setCollapsed(false)}
          alreadyAnswered={alreadyAnswered}
          chapterDisplay={chapterDisplay}
        />

        {submittedReply && (
          <SubmittedComparisonOverlay
            reply={submittedReply}
            onClose={async () => {
              setSubmittedReply(null);
              await onSubmitted?.();
            }}
          />
        )}
      </>
    );
  }

  return (
    <div
      className="mt-5 rounded-[28px] border p-5 shadow-sm"
      style={{
        borderColor: "#c4b5fd",
        background:
          "linear-gradient(135deg, #ffffff 0%, #f5f3ff 50%, #eff6ff 100%)",
      }}
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-violet-100 bg-white text-3xl shadow-sm">
            🎙️
          </div>

          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-violet-300 bg-white px-4 py-1.5 text-xs font-black text-violet-800 shadow-sm">
              <span>⚔️</span>
              <span>Rate me request</span>
            </div>

            <h3 className="mt-3 text-xl font-black text-slate-950">
              Awaiting human verdict
            </h3>

            <p className="mt-1 text-sm font-semibold text-slate-600">
              Mark the sentence, then fine-tune words and letters.
            </p>

            {chapterDisplay && (
              <div className="mt-2 inline-flex max-w-full rounded-full border border-violet-100 bg-white/80 px-3 py-1 text-xs font-black text-slate-500">
                <span className="truncate">{chapterDisplay}</span>
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 space-y-2 lg:w-[240px]">
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="flex w-full items-center justify-center rounded-[18px] border border-slate-200 bg-white px-4 py-2.5 text-xs font-black text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-950"
          >
            Collapse
          </button>

          {hasAudio && canPlay && (
            <button
              type="button"
              onClick={playRecording}
              disabled={loadingAudio}
              className="flex w-full items-center justify-center gap-2 rounded-[22px] border bg-white px-5 py-4 text-sm font-black shadow-sm transition hover:-translate-y-0.5 disabled:opacity-60"
              style={{
                borderColor: "#c4b5fd",
                color: "#4c1d95",
              }}
            >
              <span>{loadingAudio ? "…" : "▶"}</span>
              <span>{loadingAudio ? "Loading" : "Play recording"}</span>
            </button>
          )}

          {hasAudio && !canPlay && (
            <div className="rounded-[22px] border border-violet-200 bg-white px-5 py-4 text-center shadow-sm">
              <div className="flex items-center justify-center gap-2 text-sm font-black text-slate-950">
                <span>🔒</span>
                <span>Log in to listen</span>
              </div>
            </div>
          )}

          {!hasAudio && (
            <div className="rounded-[22px] border border-slate-200 bg-white px-5 py-4 text-center text-sm font-black text-slate-500 shadow-sm">
              No recording
            </div>
          )}
        </div>
      </div>

      {content && (
        <div className="mt-5 rounded-2xl border border-violet-100 bg-white/90 px-4 py-3">
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-violet-500">
            User asks
          </div>

          <p className="mt-1 select-text text-[15px] font-semibold leading-7 text-slate-900 whitespace-pre-wrap break-words">
            {content}
          </p>
        </div>
      )}

      {canInteract ? (
        <div
          className="mt-5 rounded-[24px] border p-4 shadow-sm transition-colors"
          style={getTintStyle(sentenceRating)}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-sm font-black text-slate-950">
                Sentence verdict
              </div>

              <div className="text-xs font-semibold text-slate-500">
                Green by default. Change if the whole attempt needs attention.
              </div>
            </div>

            <CircleRadioGroup
              value={sentenceRating}
              onChange={setSentenceRating}
            />
          </div>

          <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
            <div className="flex flex-wrap items-end gap-x-7 gap-y-6">
              {ratings.map((word) => (
                <div
                  key={`${word.word}-${word.index}`}
                  className="flex flex-col items-center gap-2 rounded-2xl border px-3 py-3 shadow-sm transition-colors"
                  style={getTintStyle(word.rating)}
                >
                  <div className="flex items-center gap-1">
                    {word.letters.map((letter) => (
                      <LetterButton
                        key={`${word.index}-${letter.index}`}
                        letter={letter}
                        onClick={() =>
                          cycleLetterRating(word.index, letter.index)
                        }
                      />
                    ))}
                  </div>

                  <CircleRadioGroup
                    compact
                    value={word.rating}
                    onChange={(next) => setWordRating(word.index, next)}
                  />
                </div>
              ))}
            </div>
          </div>

          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            className="mt-4 w-full min-h-[82px] rounded-2xl border bg-white px-4 py-3 text-sm font-semibold outline-none resize-y"
            style={{
              borderColor: "#e2e8f0",
              color: "#0f172a",
            }}
            placeholder="Optional note..."
          />

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={submitRating}
              disabled={submitting}
              className="rounded-2xl px-6 py-3 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 disabled:opacity-60"
              style={{
                background: "#7c3aed",
                boxShadow: "0 14px 28px rgba(124,58,237,0.25)",
                minWidth: 160,
              }}
            >
              {submitting ? "Submitting…" : "Submit verdict"}
            </button>
          </div>

          {submitError && (
            <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
              {submitError}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm font-bold text-slate-700">
          🔒 Log in to leave your human verdict.
        </div>
      )}

      {audioError && (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
          {audioError}
        </div>
      )}

      {submittedReply && (
        <SubmittedComparisonOverlay
          reply={submittedReply}
          onClose={async () => {
            setSubmittedReply(null);
            await onSubmitted?.();
          }}
        />
      )}
    </div>
  );
}